import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, apiError } from "@/lib/api";
import { Users, Barbell, ChartLine, ArrowRight, Broom, Warning, X, ArrowsClockwise, CurrencyDollar } from "@phosphor-icons/react";
import EmailPanel from "@/components/EmailPanel";
import CoachNotificationSettings from "@/components/CoachNotificationSettings";

export default function CoachOverview() {
  const [clients, setClients] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [wiping, setWiping] = useState(false);
  const [wipeMsg, setWipeMsg] = useState("");
  const [purgePreview, setPurgePreview] = useState(null); // { count, sample: [] } | null
  const [purging, setPurging] = useState(false);
  const [syncPreview, setSyncPreview] = useState(null); // { would_update, already_in_sync, skipped_no_plan, sample_update: [] } | null
  const [syncing, setSyncing] = useState(false);
  const [reconcilePreview, setReconcilePreview] = useState(null); // { total_founders, changes_planned: [], counter_before, counter_would_become } | null
  const [reconciling, setReconciling] = useState(false);

  const loadRoster = async () => {
    try {
      const [c, p] = await Promise.all([http.get("/coach/clients"), http.get("/coach/programs")]);
      setClients(c.data);
      setPrograms(p.data);
    } catch (e) { setErr(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadRoster(); }, []);

  const wipeTestUsers = async () => {
    if (!window.confirm(
      "Delete ALL test-artefact user accounts (smoke-*, cc-*, rules6-*, etc.)?\n\n" +
      "Safe: your coach account and jimmy+testclient are preserved. Real paying clients are never touched. Founder counter resets to 0."
    )) return;
    setWiping(true);
    setWipeMsg("");
    setErr("");
    try {
      const { data } = await http.post("/coach/admin/wipe-test-users");
      setWipeMsg(`✓ Wiped ${data.deleted} test users. Founder counter reset. Refreshing roster...`);
      await loadRoster();
    } catch (e) {
      setErr(apiError(e, "Cleanup failed."));
    } finally { setWiping(false); }
  };

  const previewPurge = async () => {
    setErr("");
    setWipeMsg("");
    setPurging(true);
    try {
      const { data } = await http.post("/coach/admin/purge-non-paying-clients", { dry_run: true });
      setPurgePreview({ count: data.count, sample: data.sample || [] });
    } catch (e) {
      setErr(apiError(e, "Preview failed."));
    } finally { setPurging(false); }
  };

  const confirmPurge = async () => {
    setPurging(true);
    setErr("");
    try {
      const { data } = await http.post("/coach/admin/purge-non-paying-clients", { dry_run: false });
      setWipeMsg(`✓ Purged ${data.deleted} non-paying clients. Refreshing roster...`);
      setPurgePreview(null);
      await loadRoster();
    } catch (e) {
      setErr(apiError(e, "Purge failed."));
    } finally { setPurging(false); }
  };

  const previewHabitSync = async () => {
    setErr("");
    setWipeMsg("");
    setSyncing(true);
    try {
      const { data } = await http.post("/coach/admin/sync-lazy-lean-habits-all", { dry_run: true });
      setSyncPreview(data);
    } catch (e) {
      setErr(apiError(e, "Preview failed."));
    } finally { setSyncing(false); }
  };

  const confirmHabitSync = async () => {
    setSyncing(true);
    setErr("");
    try {
      const { data } = await http.post("/coach/admin/sync-lazy-lean-habits-all", { dry_run: false });
      setWipeMsg(
        `✓ Synced ${data.updated} client plan${data.updated === 1 ? "" : "s"} to the latest 7 Lazy Lean Habits.` +
        (data.already_in_sync ? ` ${data.already_in_sync} already up to date.` : "") +
        (data.skipped_no_plan ? ` ${data.skipped_no_plan} skipped (no active plan).` : "")
      );
      setSyncPreview(null);
    } catch (e) {
      setErr(apiError(e, "Sync failed."));
    } finally { setSyncing(false); }
  };

  // Founder-number reconciliation — closes duplicate/gap founder_numbers by
  // rewriting them chronologically (earliest founder_minted_at → #1, etc.).
  // Was added Feb 2026 to fix the post-wipe #1-collision bug found in prod.
  const previewReconcile = async () => {
    setErr("");
    setWipeMsg("");
    setReconciling(true);
    try {
      const { data } = await http.post("/coach/admin/reconcile-founders", { dry_run: true });
      setReconcilePreview(data);
    } catch (e) {
      setErr(apiError(e, "Preview failed."));
    } finally { setReconciling(false); }
  };

  const confirmReconcile = async () => {
    setReconciling(true);
    setErr("");
    try {
      const { data } = await http.post("/coach/admin/reconcile-founders", { dry_run: false });
      setWipeMsg(
        `✓ Reconciled ${data.changes_applied.length} founder number${data.changes_applied.length === 1 ? "" : "s"}. ` +
        `Counter is now ${data.counter_after}. Refreshing roster...`
      );
      setReconcilePreview(null);
      await loadRoster();
    } catch (e) {
      setErr(apiError(e, "Reconcile failed."));
    } finally { setReconciling(false); }
  };

  const assigned = clients.filter((c) => c.active_program).length;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="id-label mb-3">— Coach Command Center —</div>
      <h1 className="id-h1 mb-2">Overview</h1>
      <p className="text-zinc-400 font-manrope mb-10">Your roster at a glance.</p>

      {err && <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-6" data-testid="overview-error">{err}</div>}
      {wipeMsg && <div className="border border-[#22C55E] bg-[#22C55E]/10 px-4 py-3 text-sm text-[#22C55E] mb-6" data-testid="wipe-success">{wipeMsg}</div>}

      <CoachNotificationSettings />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12" data-testid="coach-stats">
        <Stat label="Active Clients" value={clients.length} icon={Users} testid="stat-clients" />
        <Stat label="Programs Built" value={programs.length} icon={Barbell} testid="stat-programs" />
        <Stat label="Assigned" value={`${assigned} / ${clients.length || 0}`} icon={ChartLine} testid="stat-assigned" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link to="/coach/revenue" className="id-card hover:border-[#FF3333] transition-colors duration-150 group" data-testid="quick-revenue">
          <div className="flex items-center justify-between">
            <div>
              <div className="id-label mb-2 text-[#FF3333]">Revenue</div>
              <div className="font-oswald text-2xl font-semibold uppercase">Weekly Snapshot</div>
              <p className="text-zinc-500 text-sm mt-1 font-manrope">MRR · churn · funnel · founder slots</p>
            </div>
            <CurrencyDollar size={28} className="text-zinc-600 group-hover:text-[#FF3333]" weight="bold" />
          </div>
        </Link>

        <Link to="/coach/clients" className="id-card hover:border-[#FF3333] transition-colors duration-150 group" data-testid="quick-clients">
          <div className="flex items-center justify-between">
            <div>
              <div className="id-label mb-2">Clients</div>
              <div className="font-oswald text-2xl font-semibold uppercase">Manage Roster</div>
              <p className="text-zinc-500 text-sm mt-1 font-manrope">Assign programs · review progress · message</p>
            </div>
            <ArrowRight size={28} className="text-zinc-600 group-hover:text-[#FF3333]" weight="bold" />
          </div>
        </Link>

        <Link to="/coach/programs" className="id-card hover:border-[#FF3333] transition-colors duration-150 group" data-testid="quick-programs">
          <div className="flex items-center justify-between">
            <div>
              <div className="id-label mb-2">Programs</div>
              <div className="font-oswald text-2xl font-semibold uppercase">Build Programs</div>
              <p className="text-zinc-500 text-sm mt-1 font-manrope">Workout templates · multi-week splits</p>
            </div>
            <ArrowRight size={28} className="text-zinc-600 group-hover:text-[#FF3333]" weight="bold" />
          </div>
        </Link>
      </div>

      <div className="mt-6">
        <EmailPanel />
      </div>

      {/* Coach admin — one-click cleanup of accumulated test data. Safe & idempotent. */}
      <div className="mt-6 id-card border-[#333]" data-testid="admin-wipe-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="id-label text-zinc-400 mb-1 flex items-center gap-2">
              <Broom size={12} weight="fill" /> Admin · Cleanup
            </div>
            <div className="font-oswald text-lg text-white uppercase tracking-wide">Wipe test users</div>
            <p className="text-zinc-500 text-xs font-manrope mt-1 leading-relaxed max-w-xl">
              Deletes accounts left over from smoke tests + testing bots (smoke-*, cc-*, etc.).
              Preserves your coach account, jimmy+testclient, and any real paying members. Resets Founder counter to 0.
            </p>
          </div>
          <button
            onClick={wipeTestUsers}
            disabled={wiping}
            data-testid="wipe-test-users-btn"
            className="id-btn-ghost border-[#FF3333] text-[#FF3333] hover:bg-[#FF3333] hover:text-white disabled:opacity-40 shrink-0"
          >
            {wiping ? "Wiping..." : "Wipe now"}
          </button>
        </div>
      </div>

      {/* Coach admin — reconcile founder numbers. Rewrites duplicate/gap
          founder_numbers based on strict chronological order. Fixes the
          post-wipe #1-collision case where a preserved real founder + a
          fresh signup both end up with founder_number=1. */}
      <div className="mt-4 id-card border-[#333]" data-testid="admin-reconcile-founders-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="id-label text-zinc-400 mb-1 flex items-center gap-2">
              <ArrowsClockwise size={12} weight="fill" /> Admin · Founders
            </div>
            <div className="font-oswald text-lg text-white uppercase tracking-wide">Reconcile founder numbers</div>
            <p className="text-zinc-500 text-xs font-manrope mt-1 leading-relaxed max-w-xl">
              Rewrites duplicate / out-of-order founder_numbers in strict
              chronological order (earliest minted → #1, next → #2, up to #20).
              Also syncs the counter. Idempotent — safe to run anytime.
            </p>
          </div>
          <button
            onClick={previewReconcile}
            disabled={reconciling}
            data-testid="reconcile-founders-preview-btn"
            className="id-btn-ghost border-[#FF3333] text-[#FF3333] hover:bg-[#FF3333] hover:text-white disabled:opacity-40 shrink-0"
          >
            {reconciling ? "Working..." : "Preview"}
          </button>
        </div>

        {reconcilePreview && (
          <div className="mt-4 border border-[#FF3333] bg-[#FF3333]/5 p-4" data-testid="reconcile-preview">
            <div className="text-sm font-manrope text-white mb-2">
              <span className="font-bold">{reconcilePreview.total_founders}</span> founder{reconcilePreview.total_founders === 1 ? "" : "s"} total ·{" "}
              <span className="font-bold text-[#FF3333]">{reconcilePreview.changes_planned.length}</span> change{reconcilePreview.changes_planned.length === 1 ? "" : "s"} to apply ·
              counter <span className="text-zinc-400">{reconcilePreview.counter_before}</span> → <span className="text-white font-bold">{reconcilePreview.counter_would_become}</span>
            </div>
            {reconcilePreview.changes_planned.length > 0 ? (
              <ul className="space-y-1 max-h-48 overflow-y-auto text-xs font-manrope text-zinc-300" data-testid="reconcile-preview-list">
                {reconcilePreview.changes_planned.map((c) => (
                  <li key={c.id} className="flex justify-between gap-3 border-l-2 border-[#FF3333] pl-2 py-1">
                    <span className="truncate">{c.name || c.email}</span>
                    <span className="shrink-0 text-zinc-500">
                      #{c.old_number} → <span className="text-white">#{c.new_number}</span>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-xs font-manrope text-zinc-400 italic">Nothing to change — already reconciled.</div>
            )}
            {reconcilePreview.changes_planned.length > 0 && (
              <div className="mt-4 flex gap-2">
                <button
                  onClick={confirmReconcile}
                  disabled={reconciling}
                  data-testid="reconcile-founders-confirm-btn"
                  className="id-btn-primary text-xs"
                >
                  {reconciling ? "Applying..." : "Apply changes"}
                </button>
                <button
                  onClick={() => setReconcilePreview(null)}
                  disabled={reconciling}
                  data-testid="reconcile-founders-cancel-btn"
                  className="id-btn-ghost text-xs border-[#333]"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Coach admin — one-click bulk sync of every client's habits to the current
          canonical LAZY_LEAN_RULES. Fixes clients still on old habit templates
          (e.g. 8,000 steps → 10,000 steps) without manually opening every plan. */}
      <div className="mt-4 id-card border-[#333]" data-testid="admin-sync-habits-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="id-label text-zinc-400 mb-1 flex items-center gap-2">
              <ArrowsClockwise size={12} weight="fill" /> Admin · Habit sync
            </div>
            <div className="font-oswald text-lg text-white uppercase tracking-wide">Sync all clients to latest habits</div>
            <p className="text-zinc-500 text-xs font-manrope mt-1 leading-relaxed max-w-xl">
              Brings every active client plan into line with the current{" "}
              <span className="text-white">7 Lazy Lean Habits</span> (e.g. bumps
              old <span className="text-white">&ldquo;8,000 Steps&rdquo;</span> labels to{" "}
              <span className="text-white">&ldquo;10,000 Steps&rdquo;</span>, adds any missing habits).
              Historical check-ins are preserved. Two-step preview → confirm.
            </p>
          </div>
          <button
            onClick={previewHabitSync}
            disabled={syncing}
            data-testid="sync-habits-preview-btn"
            className="id-btn-ghost border-[#FF3333] text-[#FF3333] hover:bg-[#FF3333] hover:text-white disabled:opacity-40 shrink-0"
          >
            {syncing ? "Loading..." : "Preview sync"}
          </button>
        </div>
      </div>

      {/* Coach admin — purge non-paying clients (real-looking emails w/ no active sub). */}
      <div className="mt-4 id-card border-[#FF3333]/40" data-testid="admin-purge-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="id-label text-[#FF3333] mb-1 flex items-center gap-2">
              <Warning size={12} weight="fill" /> Admin · Danger Zone
            </div>
            <div className="font-oswald text-lg text-white uppercase tracking-wide">Purge non-paying clients</div>
            <p className="text-zinc-500 text-xs font-manrope mt-1 leading-relaxed max-w-xl">
              Deletes every client-role account with <span className="text-white">no active Stripe subscription</span> AND{" "}
              <span className="text-white">no founder number</span>. Founders, active subscribers, your test-client alias,
              and your coach account are always preserved. Two-step preview → confirm.
            </p>
          </div>
          <button
            onClick={previewPurge}
            disabled={purging}
            data-testid="purge-preview-btn"
            className="id-btn-ghost border-[#FF3333] text-[#FF3333] hover:bg-[#FF3333] hover:text-white disabled:opacity-40 shrink-0"
          >
            {purging ? "Loading..." : "Preview purge"}
          </button>
        </div>
      </div>

      {/* Purge confirmation modal */}
      {purgePreview && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          data-testid="purge-modal"
          onClick={() => !purging && setPurgePreview(null)}
        >
          <div
            className="bg-[#0F0F0F] border border-[#FF3333]/60 max-w-2xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 border-b border-[#262626]">
              <div>
                <div className="id-label text-[#FF3333] mb-1 flex items-center gap-2">
                  <Warning size={12} weight="fill" /> Confirm Purge
                </div>
                <div className="font-oswald text-2xl text-white uppercase tracking-wide">
                  Delete {purgePreview.count} account{purgePreview.count === 1 ? "" : "s"}?
                </div>
                <p className="text-zinc-500 text-xs font-manrope mt-1">
                  These accounts have no active subscription and no founder number.
                </p>
              </div>
              <button
                onClick={() => !purging && setPurgePreview(null)}
                disabled={purging}
                className="text-zinc-500 hover:text-white shrink-0"
                data-testid="purge-modal-close"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            {purgePreview.count === 0 ? (
              <div className="p-8 text-center">
                <p className="text-zinc-400 font-manrope">
                  Nothing to purge — your roster is clean. 🎯
                </p>
              </div>
            ) : (
              <div className="overflow-y-auto flex-1 p-5" data-testid="purge-modal-list">
                <div className="id-label text-zinc-400 mb-2">Accounts that will be deleted:</div>
                <ul className="space-y-1 font-manrope text-sm">
                  {purgePreview.sample.map((u, i) => (
                    <li key={i} className="flex items-baseline gap-3 text-zinc-300">
                      <span className="text-zinc-600 text-xs w-6 shrink-0 text-right">{i + 1}.</span>
                      <span className="text-white break-all">{u.email}</span>
                      {u.name && <span className="text-zinc-500 text-xs">({u.name})</span>}
                    </li>
                  ))}
                </ul>
                {purgePreview.count > purgePreview.sample.length && (
                  <p className="text-zinc-500 text-xs mt-3 italic">
                    ...and {purgePreview.count - purgePreview.sample.length} more (not shown).
                  </p>
                )}
              </div>
            )}

            <div className="p-5 border-t border-[#262626] flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={() => setPurgePreview(null)}
                disabled={purging}
                data-testid="purge-cancel-btn"
                className="id-btn-ghost border-[#333] text-zinc-300 hover:border-white hover:text-white disabled:opacity-40"
              >
                Cancel
              </button>
              {purgePreview.count > 0 && (
                <button
                  onClick={confirmPurge}
                  disabled={purging}
                  data-testid="purge-confirm-btn"
                  className="id-btn-primary bg-[#FF3333] text-white hover:bg-[#E62929] disabled:opacity-40"
                >
                  {purging ? "Deleting..." : `Yes, delete ${purgePreview.count} account${purgePreview.count === 1 ? "" : "s"}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {loading && <div className="id-label text-zinc-500 mt-10 id-pulse">Loading roster...</div>}

      {/* Habit sync confirmation modal */}
      {syncPreview && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          data-testid="sync-habits-modal"
          onClick={() => !syncing && setSyncPreview(null)}
        >
          <div
            className="bg-[#0F0F0F] border border-[#FF3333]/60 max-w-2xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 border-b border-[#262626]">
              <div>
                <div className="id-label text-[#FF3333] mb-1 flex items-center gap-2">
                  <ArrowsClockwise size={12} weight="fill" /> Confirm Habit Sync
                </div>
                <div className="font-oswald text-2xl text-white uppercase tracking-wide">
                  Update {syncPreview.would_update} client plan{syncPreview.would_update === 1 ? "" : "s"}?
                </div>
                <p className="text-zinc-500 text-xs font-manrope mt-1">
                  {syncPreview.already_in_sync ? `${syncPreview.already_in_sync} already up to date. ` : ""}
                  {syncPreview.skipped_no_plan ? `${syncPreview.skipped_no_plan} skipped (no active plan). ` : ""}
                  Existing habit IDs are preserved so past check-ins keep counting.
                </p>
              </div>
              <button
                onClick={() => !syncing && setSyncPreview(null)}
                disabled={syncing}
                className="text-zinc-500 hover:text-white shrink-0"
                data-testid="sync-habits-modal-close"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            {syncPreview.would_update === 0 ? (
              <div className="p-8 text-center">
                <p className="text-zinc-400 font-manrope">
                  Every active client plan is already in sync. 🎯
                </p>
              </div>
            ) : (
              <div className="overflow-y-auto flex-1 p-5" data-testid="sync-habits-modal-list">
                <div className="id-label text-zinc-400 mb-2">Clients that will be updated:</div>
                <ul className="space-y-3 font-manrope text-sm">
                  {(syncPreview.sample_update || []).map((u, i) => (
                    <li key={i} className="border-l-2 border-[#FF3333]/40 pl-3">
                      <div className="flex items-baseline gap-2 text-zinc-300">
                        <span className="text-zinc-600 text-xs w-6 shrink-0 text-right">{i + 1}.</span>
                        <span className="text-white break-all">{u.email}</span>
                        {u.name && <span className="text-zinc-500 text-xs">({u.name})</span>}
                      </div>
                      <div className="ml-8 mt-1 space-y-0.5 text-xs">
                        {u.updated && u.updated.length > 0 && (
                          <div className="text-amber-400">
                            <span className="text-zinc-500">Updates:</span> {u.updated.join(" · ")}
                          </div>
                        )}
                        {u.added && u.added.length > 0 && (
                          <div className="text-emerald-400">
                            <span className="text-zinc-500">Adds:</span> {u.added.join(" · ")}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                {syncPreview.would_update > (syncPreview.sample_update || []).length && (
                  <p className="text-zinc-500 text-xs mt-3 italic">
                    ...and {syncPreview.would_update - (syncPreview.sample_update || []).length} more (not shown).
                  </p>
                )}
              </div>
            )}

            <div className="p-5 border-t border-[#262626] flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={() => setSyncPreview(null)}
                disabled={syncing}
                data-testid="sync-habits-cancel-btn"
                className="id-btn-ghost border-[#333] text-zinc-300 hover:border-white hover:text-white disabled:opacity-40"
              >
                Cancel
              </button>
              {syncPreview.would_update > 0 && (
                <button
                  onClick={confirmHabitSync}
                  disabled={syncing}
                  data-testid="sync-habits-confirm-btn"
                  className="id-btn-primary bg-[#FF3333] text-white hover:bg-[#E62929] disabled:opacity-40"
                >
                  {syncing ? "Syncing..." : `Yes, sync ${syncPreview.would_update} plan${syncPreview.would_update === 1 ? "" : "s"}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, testid }) {
  return (
    <div className="id-card" data-testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="id-label">{label}</div>
        <Icon size={20} weight="bold" className="text-[#FF3333]" />
      </div>
      <div className="id-stat">{value}</div>
    </div>
  );
}
